import * as React from "react";
import List from "@mui/material/List";
import ListItemText from "@mui/material/ListItemText";
import { Category, ViewNode, useAppConfiguration } from "../providers/AppConfiguration";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import CategoryIcon from "@mui/icons-material/Category";
import Collapse from "@mui/material/Collapse";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ListIcon from "@mui/icons-material/List";
import SearchIcon from "@mui/icons-material/Search";
import DownloadsIcon from "@mui/icons-material/Downloading";
import DownloadDoneIcon from "@mui/icons-material/DownloadForOffline";
import RecentChangesIcon from "@mui/icons-material/AccessTime";
import { useLiveQuery } from "dexie-react-hooks";
import ListItem from "@mui/material/ListItem";
import Badge from "@mui/material/Badge";
import { db } from "../db";
import { CircularProgress } from "@mui/material";

export interface CategoryDrawerProps {
  selectedView: ViewNode | undefined;
  onViewSelected: (v: ViewNode) => any;
}

interface ViewCategoryListItemProps {
  category: Category;
  selectedView: ViewNode | undefined;
  expanded: boolean;
  onViewSelected: (view: ViewNode) => void;
  onCategoryExpanded: (cat: Category, expanded: boolean) => void;
}

interface ViewListItemProps {
  view: ViewNode;
  selected: boolean;
  indent?: boolean;
  onViewSelected: (view: ViewNode) => void;
}

const iconMap: Record<string, React.ReactNode> = {
  search: <SearchIcon />,
  downloads: <DownloadsIcon />,
  downloads_done: <DownloadDoneIcon />,
  recent_changes: <RecentChangesIcon />,
};

function ViewListItem(props: ViewListItemProps) {
  const { view, selected, onViewSelected, indent } = props;

  const badge = useLiveQuery(async () => {
    if (view.name === "Download Queue") {
      return db.documents.where("download_order").aboveOrEqual(0).count();
    }
    // No badge.
    return undefined;
  }, [view]);

  const icon = (view.icon && iconMap[view.icon]) ?? <ListIcon />;

  return (
    <ListItem disablePadding>
      <ListItemButton sx={{ pl: indent ? 4 : 2 }} onClick={() => onViewSelected(view)} selected={selected}>
        <ListItemIcon>{badge ? <Badge badgeContent={badge}>{icon}</Badge> : icon}</ListItemIcon>
        <ListItemText primary={view.name} />
      </ListItemButton>
    </ListItem>
  );
}

function isSingleLevelCateogry(category: Category) {
  if (category.views.length !== 1) {
    return false;
  }
  const query = category.views[0].query;
  return typeof query == "string" || (Array.isArray(query) && query.length === 0);
}

function ViewCatagoryListItem(props: ViewCategoryListItemProps) {
  const { category, selectedView, expanded, onViewSelected, onCategoryExpanded } = props;

  const singleLevelCat = React.useMemo(() => isSingleLevelCateogry(category), [category]);

  if (singleLevelCat) {
    // Its a singleton view.  collapse it.
    const view = category.views[0];
    return <ViewListItem key={view.name} view={view} selected={selectedView === view} onViewSelected={onViewSelected} />;
  }

  return (
    <div>
      <ListItem disablePadding>
        <ListItemButton
          onClick={() => {
            onCategoryExpanded(category, !expanded);
          }}
        >
          <ListItemIcon>
            <CategoryIcon />
          </ListItemIcon>
          <ListItemText primary={category.name} />
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
      </ListItem>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {category.views.map((view) => (
            <ViewListItem
              key={view.name}
              indent
              view={view}
              onViewSelected={props.onViewSelected}
              selected={view === props.selectedView}
            />
          ))}
        </List>
      </Collapse>
    </div>
  );
}

export default function ViewList(props: CategoryDrawerProps) {
  const [expanded, setExpanded] = React.useState<string>();

  const appConfig = useAppConfiguration();

  if (appConfig.categories.length === 0) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "1em" }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <List>
      {appConfig.categories.map((category) => (
        <ViewCatagoryListItem
          key={category.name}
          category={category}
          selectedView={props.selectedView}
          onCategoryExpanded={(_cat, expanded) => setExpanded(expanded ? category.name : undefined)}
          onViewSelected={props.onViewSelected}
          expanded={expanded === category.name}
        />
      ))}
    </List>
  );
}
